if (!customElements.get('product-showcase-slider')) {
  customElements.define(
    'product-showcase-slider',
    class ProductShowcaseSlider extends HTMLElement {
      constructor() {
        super();

        this.sliderInstance = null;
        this.sliderControls = this.querySelector('.swiper-controls');
        this.previousButton = this.querySelector(
          '[data-product-showcase-prev]'
        );
        this.nextButton = this.querySelector('[data-product-showcase-next]');
        this.items = Number(this.dataset.items) || 0;
        this.initializing = false;
        this.initTimer = null;
        this.paginationFrame = null;
        this.resizeObserver = null;

        this.handlePreviousClick = this.handlePreviousClick.bind(this);
        this.handleNextClick = this.handleNextClick.bind(this);
        this.handleSlideChange = this.handleSlideChange.bind(this);
        this.handleResize = this.handleResize.bind(this);
      }

      connectedCallback() {
        if (this.sliderInstance || this.initializing) return;

        this.initializing = true;
        this.initializeWhenThemeIsReady();
      }

      disconnectedCallback() {
        window.clearTimeout(this.initTimer);
        window.cancelAnimationFrame(this.paginationFrame);
        this.resizeObserver?.disconnect();
        window.removeEventListener('resize', this.handleResize);

        this.previousButton?.removeEventListener(
          'click',
          this.handlePreviousClick
        );
        this.nextButton?.removeEventListener('click', this.handleNextClick);

        const swiper = this.sliderInstance?.slider;

        if (swiper && !swiper.destroyed) {
          swiper.off('realIndexChange', this.handleSlideChange);

          if (typeof swiper.destroy === 'function') {
            swiper.destroy(true, true);
          }
        }

        this.sliderInstance = null;
        this.initializing = false;
      }

      static get observedAttributes() {
        return ['selected-index'];
      }

      get selectedIndex() {
        return Number.parseInt(this.getAttribute('selected-index'), 10) || 0;
      }

      set selectedIndex(index) {
        this.setAttribute('selected-index', String(index));
      }

      initializeWhenThemeIsReady(attempt = 0) {
        const themeReady =
          window.FoxTheme?.Carousel &&
          window.FoxTheme?.Swiper;

        if (themeReady) {
          this.initSlider();
          this.initializing = false;
          return;
        }

        if (attempt >= 100) {
          this.initializing = false;
          console.warn(
            '[Product showcase slider] FoxTheme.Carousel was not available.'
          );
          return;
        }

        this.initTimer = window.setTimeout(() => {
          this.initializeWhenThemeIsReady(attempt + 1);
        }, 50);
      }

      initSlider() {
        if (this.sliderInstance) return;

        const swiperModules = [
          window.FoxTheme.Swiper.Autoplay,
          window.FoxTheme.Swiper.EffectFade,
        ].filter(Boolean);

        const sliderOptions = {
          slidesPerView: 1,
          spaceBetween: 10,
          loop: this.items > 1,
          grabCursor: this.items > 1,
          allowTouchMove: this.items > 1,
          threshold: 2,
          effect: 'fade',
          fadeEffect: {
            crossFade: true,
          },
          on: {
            init: () => this.handleAfterInit(),
          },
        };

        const paginationElement =
          this.sliderControls?.querySelector('.swiper-pagination');

        if (paginationElement) {
          sliderOptions.pagination = {
            el: paginationElement,
            clickable: true,
          };
        }

        const autoplayDelay = Number.parseInt(this.dataset.autoplay, 10);

        if (autoplayDelay > 0 && this.items > 1) {
          sliderOptions.autoplay = {
            delay: autoplayDelay,
            disableOnInteraction: false,
          };
        }

        this.sliderInstance = new window.FoxTheme.Carousel(
          this,
          sliderOptions,
          swiperModules
        );

        this.sliderInstance.init();
        this.applyPaginationColor();

        const swiper = this.sliderInstance.slider;
        const initialIndex = swiper.realIndex || 0;

        swiper.on('realIndexChange', this.handleSlideChange);

        this.previousButton?.addEventListener(
          'click',
          this.handlePreviousClick
        );
        this.nextButton?.addEventListener('click', this.handleNextClick);

        this.selectedIndex = initialIndex;

        const selectedSlide = swiper.slides[swiper.activeIndex];
        this.activateSlide(selectedSlide);
        this.setupPaginationPositioning();
        this.schedulePaginationPosition(selectedSlide);
      }

      handlePreviousClick(event) {
        const swiper = this.sliderInstance?.slider;

        if (!swiper || swiper.destroyed || swiper.animating) return;

        event.preventDefault();
        swiper.slidePrev();
      }

      handleNextClick(event) {
        const swiper = this.sliderInstance?.slider;

        if (!swiper || swiper.destroyed || swiper.animating) return;

        event.preventDefault();
        swiper.slideNext();
      }

      handleAfterInit() {
        this.applyPaginationColor();

        const activeBullet = this.sliderControls?.querySelector(
          '.swiper-pagination-bullet-active'
        );

        if (!activeBullet) return;

        activeBullet.classList.remove('swiper-pagination-bullet-active');
        activeBullet.offsetHeight;
        activeBullet.classList.add('swiper-pagination-bullet-active');
      }

      applyPaginationColor() {
        if (!this.sliderControls) return;

        const color =
          this.sliderControls.dataset.paginationColor?.trim() || '#ffffff';
        const pagination =
          this.sliderControls.querySelector('.swiper-pagination');

        /*
         * Wildpeak colors the animated active blob through
         * --swiper-pagination-color on .swiper-pagination-bullet::after.
         * Apply the variable directly to every generated bullet so no
         * color-scheme variable on an ancestor can override it.
         */
        [this.sliderControls, pagination]
          .filter(Boolean)
          .forEach((element) => {
            element.style.setProperty(
              '--product-showcase-pagination-color',
              color,
              'important'
            );
            element.style.setProperty(
              '--swiper-pagination-color',
              color,
              'important'
            );
          });

        pagination
          ?.querySelectorAll('.swiper-pagination-bullet')
          .forEach((bullet) => {
            bullet.style.setProperty(
              '--product-showcase-pagination-color',
              color,
              'important'
            );
            bullet.style.setProperty(
              '--swiper-pagination-color',
              color,
              'important'
            );
          });
      }

      handleSlideChange(swiper) {
        this.selectedIndex = swiper.realIndex || 0;

        const activeSlide = swiper.slides[swiper.activeIndex];
        this.schedulePaginationPosition(activeSlide);
      }

      setupPaginationPositioning() {
        if (!this.sliderControls) return;

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => {
            this.schedulePaginationPosition();
          });
          this.resizeObserver.observe(this);
        } else {
          window.addEventListener('resize', this.handleResize);
        }

        this.querySelectorAll('.product-img').forEach((image) => {
          if (image.tagName === 'IMG' && !image.complete) {
            image.addEventListener(
              'load',
              () => this.schedulePaginationPosition(),
              { once: true }
            );
          }
        });
      }

      handleResize() {
        this.schedulePaginationPosition();
      }

      schedulePaginationPosition(slide = null) {
        if (!this.sliderControls) return;

        window.cancelAnimationFrame(this.paginationFrame);

        this.paginationFrame = window.requestAnimationFrame(() => {
          const swiper = this.sliderInstance?.slider;
          const activeSlide =
            slide || (swiper ? swiper.slides[swiper.activeIndex] : null);
          const image = activeSlide?.querySelector('.product-img');

          if (!image) return;

          const sliderRect = this.getBoundingClientRect();
          const imageRect = image.getBoundingClientRect();
          const centerX =
            imageRect.left - sliderRect.left + imageRect.width / 2;
          const belowImageY = imageRect.bottom - sliderRect.top + 16;

          this.style.setProperty(
            '--product-showcase-pagination-x',
            `${centerX}px`
          );
          this.style.setProperty(
            '--product-showcase-pagination-y',
            `${belowImageY}px`
          );
        });
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (
          name !== 'selected-index' ||
          oldValue === null ||
          oldValue === newValue
        ) {
          return;
        }

        const previousSlides = this.querySelectorAll(
          `[data-swiper-slide-index="${oldValue}"]`
        );
        const currentSlides = this.querySelectorAll(
          `[data-swiper-slide-index="${newValue}"]`
        );

        previousSlides.forEach((slide) => this.deactivateSlide(slide));

        currentSlides.forEach((slide) => {
          window.setTimeout(() => {
            if (slide.classList.contains('swiper-slide-active')) {
              this.activateSlide(slide);
            }
          });
        });
      }

      deactivateSlide(slide) {
        slide
          .querySelectorAll('motion-element[data-text]')
          .forEach((element) => {
            if (typeof element.resetAnimation === 'function') {
              element.resetAnimation();
            }
          });
      }

      activateSlide(slide) {
        if (!slide || window.FoxTheme?.config?.motionReduced) return;

        slide.querySelectorAll('motion-element').forEach((element) => {
          if (!element) return;

          if (element.hasAttribute('hold')) {
            element.removeAttribute('hold');
          }

          if (
            !element.classList.contains('is-animated') &&
            typeof element.preInitialize === 'function' &&
            typeof element.initialize === 'function'
          ) {
            element.preInitialize();
            window.setTimeout(() => element.initialize());
            return;
          }

          if (
            element.hasAttribute('data-text') &&
            typeof element.refreshAnimation === 'function'
          ) {
            element.refreshAnimation();
          }
        });
      }
    }
  );
}
